import type { Editor } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import type { AutocompleteListItem } from '../../types';
import { generateBlockId } from './blockContext';

export interface InsertRange {
  from: number;
  to: number;
}

export interface InsertOptions {
  showDescriptions?: boolean;
}

/**
 * Exit the nearest ancestor handlebarsBlock of the given type.
 * Moves cursor after the block. Used when user types {{/blockType}}.
 */
export function exitBlock(
  editor: Editor,
  range: InsertRange,
  blockType: string
): void {
  const { state } = editor;
  const { tr } = state;
  const $pos = state.doc.resolve(range.from);

  // Delete the typed text first
  tr.delete(range.from, range.to);

  // Find the nearest ancestor handlebarsBlock of this type
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === 'handlebarsBlock' && node.attrs.blockType === blockType) {
      // Move cursor after the block
      const afterBlock = $pos.after(depth);
      // Adjust position for the deletion we just did
      const adjustment = range.to - range.from;
      const adjustedPos = afterBlock - adjustment;

      // Ensure there's a paragraph after the block to land in
      const $afterPos = tr.doc.resolve(Math.min(adjustedPos, tr.doc.content.size));
      if ($afterPos.parent.type.name === 'doc' || $afterPos.nodeAfter === null) {
        tr.insert(adjustedPos, state.schema.nodes.paragraph.create());
        tr.setSelection(TextSelection.create(tr.doc, adjustedPos + 1));
      } else {
        tr.setSelection(TextSelection.create(tr.doc, adjustedPos));
      }

      editor.view.dispatch(tr);
      return;
    }
  }

  // No matching block found — just delete the typed text
  editor.view.dispatch(tr);
}

/**
 * Insert a wrapping block node containing an empty paragraph.
 * Places cursor inside the block.
 */
export function insertBlock(
  editor: Editor,
  range: InsertRange,
  item: AutocompleteListItem,
  options: InsertOptions
): void {
  const blockId = generateBlockId();

  editor
    .chain()
    .focus()
    .deleteRange(range)
    .insertContent({
      type: 'handlebarsBlock',
      attrs: {
        blockType: item.blockType,
        variable: item.value,
        blockId,
        description: options.showDescriptions ? (item.description || null) : null,
        variableDescription: options.showDescriptions ? ((item.metadata?.variableDescription as string) || null) : null,
        isValid: true,
        error: null,
        nonEditable: item.nonEditable || false,
      },
      content: [
        {
          type: 'paragraph',
        },
      ],
    })
    .run();

  // Place cursor inside the block's empty paragraph
  // Structure: ...blockStart, paragraphStart, cursor, paragraphEnd, blockEnd...
  // We need to find the position inside the newly inserted block
  const { state } = editor;
  const { from } = state.selection;

  // After insertContent, cursor is after the block. Move it inside.
  // Walk backwards to find the empty paragraph inside the block.
  const $pos = state.doc.resolve(from);
  for (let d = $pos.depth; d > 0; d--) {
    const node = $pos.node(d);
    if (node.type.name === 'handlebarsBlock' && node.attrs.blockId === blockId) {
      // Found the block — position cursor inside the first (empty) paragraph
      const blockPos = $pos.before(d);
      // blockPos + 1 = start of first child, + 1 = inside the paragraph
      editor.commands.setTextSelection(blockPos + 2);
      return;
    }
  }

  // Fallback: scan the doc for the block we just inserted
  state.doc.descendants((node, pos) => {
    if (node.type.name === 'handlebarsBlock' && node.attrs.blockId === blockId) {
      // pos + 1 = inside the block, + 1 = inside the first paragraph
      editor.commands.setTextSelection(pos + 2);
      return false;
    }
  });
}

/**
 * Insert an inline handlebars tag (e.g., {{name}}).
 */
export function insertInlineTag(
  editor: Editor,
  range: InsertRange,
  item: AutocompleteListItem,
  options: InsertOptions
): void {
  editor
    .chain()
    .focus()
    .deleteRange(range)
    .insertContent({
      type: 'handlebarsTag',
      attrs: {
        variable: item.value,
        description: options.showDescriptions ? (item.description || null) : null,
        isValid: true,
        nonEditable: item.nonEditable || false,
      },
    })
    .insertContent(' ')
    .run();
}
